import React from "react";
import type { ReactNode } from "react";
import type { MarkdownPreviewProps } from "@uiw/react-markdown-preview";
import {
  ErrorCircleRegular,
  ImportantRegular,
  InfoRegular,
  LightbulbRegular,
  WarningRegular,
} from "@fluentui/react-icons";
import styles from "../../MarkdownContent.module.scss";

export interface IAlertConfig {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  className: string;
}

const DEFAULT_ALERT_CONFIGS = {
  NOTE: { icon: InfoRegular, label: "NOTE", className: "alertNote" },
  TIP: { icon: LightbulbRegular, label: "TIP", className: "alertTip" },
  IMPORTANT: { icon: ImportantRegular, label: "IMPORTANT", className: "alertImportant" },
  WARNING: { icon: WarningRegular, label: "WARNING", className: "alertWarning" },
  CAUTION: { icon: ErrorCircleRegular, label: "CAUTION", className: "alertCaution" },
} as const satisfies Record<string, IAlertConfig>;

const FALLBACK_ALERT: IAlertConfig = { icon: InfoRegular, label: "", className: "alertDefault" };
const ALERT_REGEX = /^\[!([A-Z][A-Z0-9_ -]*)\]\s*/i;

function getFirstTextFromParagraph(child: React.ReactNode): string | null {
  if (
    child === null ||
    typeof child !== "object" ||
    !("props" in (child as React.ReactElement)) ||
    (child as React.ReactElement).type !== "p"
  ) {
    return null;
  }

  const pChildren = (child as React.ReactElement).props.children;
  const first = Array.isArray(pChildren) ? pChildren[0] : pChildren;
  return typeof first === "string" ? first : null;
}

function detectAlertType(children: React.ReactNode): string | null {
  const childArray = Array.isArray(children) ? children : [children];

  for (const child of childArray) {
    const text = getFirstTextFromParagraph(child);
    if (text) {
      const match = text.match(ALERT_REGEX);
      if (match) {
        return match[1].toUpperCase();
      }
    }
  }

  return null;
}

function stripAlertMarker(children: React.ReactNode): React.ReactNode[] {
  const childArray = Array.isArray(children) ? children : [children];
  let stripped = false;
  let generatedKey = 0;

  return childArray
    .map((child) => {
      if (stripped) {
        return child;
      }

      const text = getFirstTextFromParagraph(child);
      if (!text) {
        return child;
      }

      stripped = true;
      const cleanedText = text.replace(ALERT_REGEX, "");
      const pChildren = (child as React.ReactElement).props.children;
      const rest = Array.isArray(pChildren) ? pChildren.slice(1) : [];
      const newChildren: React.ReactNode[] = cleanedText ? [cleanedText, ...rest] : rest;

      if (newChildren.length === 0) {
        return null;
      }

      return React.cloneElement(child as React.ReactElement, {}, ...newChildren);
    })
    .filter((c): c is React.ReactNode => c !== null && c !== undefined)
    .map((node) => {
      if (React.isValidElement(node) && node.key === null) {
        generatedKey += 1;
        return React.cloneElement(node, { key: `alert-body-${generatedKey}` });
      }

      return node;
    });
}

function resolveAlertConfig(type: string, overrides?: Record<string, IAlertConfig>): { config: IAlertConfig; label: string } {
  const configs: Record<string, IAlertConfig> = overrides
    ? { ...DEFAULT_ALERT_CONFIGS, ...overrides }
    : { ...DEFAULT_ALERT_CONFIGS };

  const config = configs[type] ?? FALLBACK_ALERT;
  const label = config.label || type.charAt(0) + type.slice(1).toLowerCase();

  return { config, label };
}

interface IBlockquoteRendererProps {
  alertConfigs?: Record<string, IAlertConfig>;
}

export function createBlockquoteRenderer({
  alertConfigs,
}: IBlockquoteRendererProps): Pick<NonNullable<MarkdownPreviewProps["components"]>, "blockquote"> {
  return {
    blockquote: ({ children, ...props }: React.BlockquoteHTMLAttributes<HTMLQuoteElement> & { children?: React.ReactNode }): ReactNode => {
      const alertType = detectAlertType(children);

      if (!alertType) {
        return <blockquote {...props}>{children}</blockquote>;
      }

      const { config, label } = resolveAlertConfig(alertType, alertConfigs);
      const IconComponent = config.icon;
      const cssClass = styles[config.className] ?? styles.alertDefault;
      const alertBodyContent = stripAlertMarker(children);

      return (
        <div className={`${styles.alert} ${cssClass}`} role="note">
          <p className={styles.alertTitle}>
            <IconComponent className={styles.alertIcon} />
            {label}
          </p>
          {alertBodyContent}
        </div>
      );
    },
  };
}

